import React from 'react'
import App, { AppContext, AppInitialProps } from 'next/app'
import { NextRouter } from 'next/router'
import getConfig from 'next/config'
import middleware from 'middleware'
import { ParsedUrlQuery } from 'querystring'
import ErrorBoundary from '@mc/error-boundary'
import { ErrorPage } from '@mc/error-page'
import { setExperiments, setExperimentsVariables } from '@mc/experiments'
import { ExperimentsProvider } from '@mc/experiments-react'
import { setFeatureFlips } from '@mc/feature-flip'
import { FeatureFlipsProvider } from '@mc/feature-flip-react'
import { setGeo, setRegion } from '@mc/geo'
import { GeoProvider } from '@mc/geo-react'
import { I18nextProvider } from '@mc/i18n'
// eslint-disable-next-line no-restricted-imports
import { mediaStyle } from '@mc/media'
import { reportError as reportBrowserError } from '@mc/monitoring'
import { Head, NewRelic, Segment, Consent } from '@mc/next-common'
import { getGrowthBookFlags } from '@mc/next-growthbook'
import { reportError } from '@mc/next-newrelic'
import { getUserAttributes } from '@mc/next-user-attributes'
import { setServer } from '@mc/track-event'
import { SegmentTrackingContext } from '@mc/tracking-context'
import { OpenAPI } from '@mc/jarvis-api'
import {
  GlobalErrorModal,
  GlobalErrorModalProvider,
} from '../components/GlobalErrorModal'
import { ReactQueryProvider } from '../components/ReactQueryProvider'
import { AuthProvider } from '../contexts/AuthContext'
import { i18nInstance } from '../i18n/i18nInstance'
import { SegmentTrackingProvider } from '../tracking/SegmentTrackingProvider'
import { TrackingAttributes } from '../tracking/jarvisTracking'

import './tailwind.css'
import './base.scss'
import './fonts.css'

require('libs/client/init-env').init() // eslint-disable-line

setServer('nextjs')

if (typeof window !== 'undefined') {
  const { publicRuntimeConfig } = getConfig()
  OpenAPI.BASE = publicRuntimeConfig.JARVIS_SERVER_URL || '/api/v1'
}

type PageProps = {
  userAttributes: ReturnType<typeof getUserAttributes>
  experiments: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperiments']
  experimentsVariables: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperimentsVariables']
  featureFlips: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookFeatureFlips']
  error?: globalThis.Error & { statusCode: number }
}

type Props =
  | (AppInitialProps<PageProps> & {
      query?: ParsedUrlQuery
    })
  | {
      serverSideInitialPropsError: true
    }

type ComponentType = React.ComponentType<PageProps> & {
  pageSettings?: {
    title?: string
    pageName?: string
    analyticsLabel?: string
    pageLoadIntegrations?: Record<string, boolean>
  }
}

class Layout extends React.Component<
  Props & { Component: ComponentType; router: NextRouter }
> {
  constructor(props: Props & { Component: ComponentType; router: NextRouter }) {
    super(props)
    try {
      if (!('serverSideInitialPropsError' in props)) {
        const {
          userAttributes,
          experiments,
          experimentsVariables,
          featureFlips,
        } = props.pageProps

        // set lib data for react
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips(featureFlips)
        setExperiments(experiments)
        setExperimentsVariables(experimentsVariables)
      }
    } catch (e) {
      reportError(e, {
        rootLayout: 'true',
        location: 'Layout constructor',
        message: 'Error occurred in _app.page.js Layout constructor',
      })
    }
  }

  static async getInitialProps(appContext: AppContext): Promise<Props> {
    const appProps: AppInitialProps<PageProps> =
      await App.getInitialProps(appContext)
    const { query, req, res } = appContext.ctx

    // runs on server only
    if (req && res) {
      try {
        middleware(req, res, appContext.router)

        const userAttributes = {
          ...getUserAttributes(appContext.ctx, appContext.router),
          platform_name: 'jarvis',
        }
        const {
          growthBookExperiments,
          growthBookExperimentsVariables,
          growthBookFeatureFlips,
        } = await getGrowthBookFlags(userAttributes, appContext.ctx)
        // set lib data for getServerSideProps
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips({ ...growthBookFeatureFlips })
        setExperiments({ ...growthBookExperiments })
        setExperimentsVariables({
          ...growthBookExperimentsVariables,
        })

        return {
          ...appProps,
          pageProps: {
            ...appProps.pageProps,
            experiments: {
              ...growthBookExperiments,
            },
            experimentsVariables: {
              ...growthBookExperimentsVariables,
            },
            featureFlips: {
              ...growthBookFeatureFlips,
            },
            userAttributes,
          },
          query,
        }
      } catch (e) {
        reportError(e, {
          rootLayout: 'true',
          location: 'getInitialProps',
          message: 'Error occurred in _app.page.js getInitialProps',
        })
        return {
          serverSideInitialPropsError: true,
        }
      }
    }

    return { ...appProps }
  }

  render() {
    if ('serverSideInitialPropsError' in this.props) {
      return <ErrorPage />
    }

    const { Component, pageProps } = this.props
    const pageTitle = Component.pageSettings?.title || 'Jarvis'
    const { userAttributes } = pageProps
    const country = userAttributes?.country
    const region = userAttributes?.region
    const experimentsProviderValues = {
      experiments: pageProps?.experiments,
      experimentsVariables: pageProps?.experimentsVariables,
    }
    const featureFlips = pageProps?.featureFlips

    const mcPageClassName = 'mc-page mc-theme-light'

    const errorBoundaryFallback = () => (
      <div className='mc-page mc-theme-light'>
        <main className='mc-page__content'>
          <GlobalErrorModal
            title={i18nInstance.t('@mc/jarvis:globalError.title')}
            description={i18nInstance.t('@mc/jarvis:globalError.description')}
            primaryActionLabel={i18nInstance.t(
              '@mc/jarvis:globalError.reloadCta',
            )}
            onPrimaryAction={() => window.location.reload()}
          />
        </main>
      </div>
    )

    return (
      <>
        <I18nextProvider i18n={i18nInstance} defaultNS='@mc/jarvis'>
          <GlobalErrorModalProvider>
            <AuthProvider>
              <ErrorBoundary
                fallback={errorBoundaryFallback}
                onError={(e) =>
                  reportBrowserError(
                    new globalThis.Error('Layout ErrorBoundary'),
                    {
                      error: String(e),
                      stack: e?.stack as string,
                    },
                  )
                }
              >
                <ReactQueryProvider>
                <SegmentTrackingProvider>
                  <TrackingAttributes product='jarvis'>
                    <FeatureFlipsProvider value={featureFlips}>
                      <ExperimentsProvider value={experimentsProviderValues}>
                        <GeoProvider value={{ country, region }}>
                          <Head>
                            <title>{pageTitle}</title>
                            <style type='text/css'>{mediaStyle}</style>
                          </Head>
                          <NewRelic />
                          <SegmentTrackingContext.Consumer>
                            {({ trackPage }) => (
                              <Segment
                                platform='next'
                                analyticsLabel={Component.pageSettings?.analyticsLabel}
                                analyticsIntegrations={
                                  Component.pageSettings?.pageLoadIntegrations
                                }
                                trackPage={trackPage}
                              />
                            )}
                          </SegmentTrackingContext.Consumer>
                          <Consent />
                          <div className={mcPageClassName}>
                            <main className='mc-page__content' id='mc-main'>
                              <Component {...pageProps} />
                            </main>
                          </div>
                        </GeoProvider>
                      </ExperimentsProvider>
                    </FeatureFlipsProvider>
                  </TrackingAttributes>
                </SegmentTrackingProvider>
                </ReactQueryProvider>
              </ErrorBoundary>
            </AuthProvider>
          </GlobalErrorModalProvider>
        </I18nextProvider>
      </>
    )
  }
}

export default Layout
